import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  BulkUpdateNotificationStatusDocument,
  DocumentForPreviewDocument,
  DocumentsDocument,
} from 'generated/resources/graphql'

import { Access, useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import DocumentNavigator from 'modules/documents/components/DocumentNavigator'
import DocumentPreview, {
  type NavigatorContentProps,
} from 'modules/documents/components/DocumentPreview'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { NotificationStatuses } from 'modules/documents/lib/enums'

interface Props {
  documentId: string
  documents: Document[]
  onClose: (id?: number) => void
  contractVersions: ContractVersions
  loading?: boolean
}

const DocumentModal = ({
  documentId,
  documents = [],
  onClose,
  contractVersions,
  loading,
}: Props) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const session = useCurrentSession()
  const [bulkUpdateNotificationStatus] = useMutation(BulkUpdateNotificationStatusDocument, {
    extractorFn: (data) => data.documentsMutations.bulkUpdateNotificationStatusDocumentsDocument,
  })
  const [document, setDocument] = useState<Document | undefined>(undefined)
  const [documentsForModal, setDocumentsForModal] = useState<Document[]>([])
  const [documentIndex, setDocumentIndex] = useState<number>(-1)

  const { data: documentForPreviewData } = useQuery(DocumentForPreviewDocument, {
    variables: {
      id: parseInt(documentId),
    },
  })

  useUnsafeEffect(() => {
    const doc = documentForPreviewData?.documents.documentsConnection.nodes[0]

    const index = documents.findIndex((d) => d.id === parseInt(documentId))

    // This logic is to handle the case when the document is not in the list of documents
    if (index !== -1) {
      setDocumentsForModal(documents)
      setDocumentIndex(index)
    } else {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      setDocumentsForModal([doc as DocumentsDocument])
      setDocumentIndex(0)
    }

    setDocument(doc)
  }, [documentForPreviewData, documentId, documents])

  const markNotificationAsViewed = (doc: Document | undefined, access: Access) => {
    if (!doc) return

    if (doc.notificationStatus !== NotificationStatuses.DeclinedPending) return

    if (doc.author?.id !== access.id) return

    bulkUpdateNotificationStatus({
      variables: { documentIds: [doc.id], notificationStatus: NotificationStatuses.Viewed },
    })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useUnsafeEffect(() => markNotificationAsViewed(document, session.access), [document, session])

  const onNavigateToOtherDocument = (target: Document | undefined) => {
    const newPathname = pathname.replace(new RegExp(`/${documentId}/?$`), `/${target?.id}`)

    navigate(newPathname)
  }

  const renderContent = (props: NavigatorContentProps) => {
    return <DocumentPreview {...props} contractVersions={contractVersions} loading={loading} />
  }

  const onCloseDocumentNavigator = () => {
    const folder_id = document?.folder?.id
    onClose(folder_id ? folder_id : undefined)
  }

  return (
    <DocumentNavigator
      content={renderContent}
      documents={documentsForModal}
      index={documentIndex}
      onClose={onCloseDocumentNavigator}
      onNavigateToOtherDocument={onNavigateToOtherDocument}
      contractVersions={contractVersions}
    />
  )
}

export default DocumentModal
